import { Content, Link } from "@backstage/core-components";
import { HomePageCompanyLogo } from "@backstage/plugin-home";
import { HomePageSearchBar } from "@backstage/plugin-search";
import { SearchContextProvider } from "@backstage/plugin-search-react";
import { usePermission } from "@backstage/plugin-permission-react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import DescriptionIcon from "@material-ui/icons/Description";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import NotificationsIcon from "@material-ui/icons/Notifications";
import EventNoteIcon from "@material-ui/icons/EventNote";
import BrushIcon from "@material-ui/icons/Brush";
import { crmonReadPermission } from "@internal/plugin-crmon";
import { circlesAccessPermission } from "@internal/plugin-circles";
import { resumeGeneratorAccessPermission } from "../../permissions";

const useStyles = makeStyles((theme) => ({
  searchBarInput: {
    maxWidth: "60vw",
    margin: "auto",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50px",
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: "none",
  },
  section: {
    padding: theme.spacing(0, 4, 4),
    maxWidth: 1200,
    margin: "0 auto",
    width: "100%",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 1),
  },
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    "& svg": {
      fontSize: 24,
      color: "#fff",
    },
  },
  mascot: {
    width: 44,
    height: 44,
    objectFit: "contain",
  },
  cardContent: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: `${theme.spacing(1.5)}px !important`,
    flexGrow: 1,
  },
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    fontSize: "0.85rem",
  },
  quickLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
}));

interface QuickLink {
  label: string;
  to: string;
  icon?: React.ReactNode;
}

interface PluginCardProps {
  title: string;
  description: string;
  to: string;
  color: string;
  icon: React.ReactNode;
  mascot?: string;
  quickLinks?: QuickLink[];
}

const PluginCard = ({
  title,
  description,
  to,
  color,
  icon,
  mascot,
  quickLinks,
}: PluginCardProps) => {
  const classes = useStyles();
  return (
    <Card className={classes.card} variant="outlined">
      <CardActionArea component={Link} to={to} style={{ flexGrow: 1 }}>
        <Box className={classes.cardHeader}>
          <Box
            className={classes.iconBox}
            style={{ backgroundColor: mascot ? "transparent" : color }}
          >
            {mascot ? (
              <img src={mascot} alt={title} className={classes.mascot} />
            ) : (
              icon
            )}
          </Box>
          <Typography
            variant="h6"
            style={{ fontWeight: 600, fontSize: "1rem" }}
          >
            {title}
          </Typography>
        </Box>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.description}>{description}</Typography>
          {quickLinks && quickLinks.length > 0 && (
            <Box className={classes.quickLinks}>
              {quickLinks.map((link) => (
                <Chip
                  key={link.to}
                  label={link.label}
                  size="small"
                  icon={link.icon as any}
                  onClick={(e) => e.stopPropagation()}
                  component={Link}
                  to={link.to}
                  clickable
                  style={{ fontSize: "0.72rem" }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const useLogoStyles = makeStyles((theme) => ({
  container: {
    margin: theme.spacing(5, 0),
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const { container } = useLogoStyles();

  const { allowed: canUseCrmly } = usePermission({
    permission: crmonReadPermission,
  });
  const { allowed: canUseCircles } = usePermission({
    permission: circlesAccessPermission,
  });
  const { allowed: canUseResumes } = usePermission({
    permission: resumeGeneratorAccessPermission,
  });

  return (
    <SearchContextProvider>
      <Content noPadding>
        <Grid container justifyContent="center" spacing={6}>
          <HomePageCompanyLogo
            className={container}
            logo={
              <img
                src="/devleaps-forge-homepage.png"
                alt="Devleaps"
                style={{ height: 100 }}
              />
            }
          />
          <Grid container item xs={12} justifyContent="center">
            <HomePageSearchBar
              InputProps={{
                classes: {
                  root: classes.searchBarInput,
                  notchedOutline: classes.searchBarOutline,
                },
              }}
              placeholder="Search"
            />
          </Grid>
          <Grid container item xs={12} justifyContent="center">
            <Box className={classes.section}>
              <Grid container spacing={3}>
                {canUseCrmly && (
                  <Grid item xs={12} sm={6} md={4}>
                    <PluginCard
                      title="Crmly"
                      description="Manage your sales pipeline, track deals, and coordinate weekly syncs."
                      to="/crmon"
                      color="#4CAF50"
                      icon={<PeopleIcon />}
                      mascot="/leapstaq-crmon-mascot.png"
                      quickLinks={[
                        {
                          label: "Pipeline",
                          to: "/crmon",
                          icon: <ViewColumnIcon />,
                        },
                        {
                          label: "Nudges",
                          to: "/crmon/nudges",
                          icon: <NotificationsIcon />,
                        },
                        {
                          label: "Weekly Sync",
                          to: "/crmon/weekly-sync",
                          icon: <EventNoteIcon />,
                        },
                      ]}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <PluginCard
                    title="Time Writing"
                    description="Log and manage your weekly hours across projects and tasks."
                    to="/odoo-timesheet"
                    color="#2196F3"
                    icon={<AccessTimeIcon />}
                  />
                </Grid>
                {canUseCircles && (
                  <Grid item xs={12} sm={6} md={4}>
                    <PluginCard
                      title="Circles"
                      description="Explore your organisation's team structure and roles."
                      to="/circles"
                      color="#9C27B0"
                      icon={<GroupWorkIcon />}
                    />
                  </Grid>
                )}
                {canUseResumes && (
                  <Grid item xs={12} sm={6} md={4}>
                    <PluginCard
                      title="Resumes"
                      description="Generate and manage professional resumes for client proposals."
                      to="/resume-generator"
                      color="#FF5722"
                      icon={<DescriptionIcon />}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <PluginCard
                    title="Forge"
                    description="Scaffolding tools to create and configure new projects and components."
                    to="/forge"
                    color="#FF9800"
                    icon={<WhatshotIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <PluginCard
                    title="Design System"
                    description="Colors, typography, spacing, logos, and brand assets for all Devleaps products."
                    to="https://design-system.devleaps.workers.dev/"
                    color="#FF4648"
                    icon={<BrushIcon />}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Content>
    </SearchContextProvider>
  );
};
